/**
 * geo-language.js - MAYAS Auto-Market Redirect, Redirect Toast, and Unmatched-Country Banner
 *
 * Loaded via <script defer> in layout/theme.liquid, before {% render 'geo-popup' %}.
 *
 * Responsibilities:
 *   1. Detect the visitor's IP country via /browsing_context_suggestions.json
 *   2. Map country → Shopify market URL prefix
 *   3. If the current URL prefix does not match → redirect (window.location.replace)
 *   4. Set a 180-day cookie (mayas_market_pref) so returning visitors skip IP detection
 *   5. Show a redirect toast after landing on the new market URL (Task E)
 *   6. Show an unmatched-country banner for countries with no configured market (Task D)
 *
 * Storage keys introduced (see report table at bottom of file):
 *   Cookie:       mayas_market_pref        - last-used market path, 180-day TTL
 *   localStorage: mayas_geo_user_explicit  - user manually chose a market, 90-day TTL
 *   localStorage: mayas_geo_banner_dismissed - unmatched-country banner dismissed, 30-day TTL
 *   sessionStorage: mayas_geo_just_redirected  - we just redirected this page load
 *   sessionStorage: mayas_geo_toast_shown      - toast already shown this session
 *   sessionStorage: mayas_geo_redirect_count   - loop guard: bail after 2 redirects
 *
 * Confirmed market URL prefixes (2026-04-18, verified against config/markets.json):
 *   DK  (default market)  →  '/'         - Danish + English DK
 *   EU  market            →  '/en-eu'    - English only; includes SE, LT, and all EU countries
 *                                          (Sweden has NO own market in Shopify - falls into EU)
 *   NO  market (norge)    →  '/en-no'    - English only
 *   GB  market            →  '/en-gb'    - English only
 *   NA  market (usa-og-canada) → '/en-na' - English only
 *   AU  market (australia-new-zealand) → '/en-au' - English only; includes NZ
 *
 * NOTE: If Marcus adds a Sweden market in Shopify Admin later, update SE entry in
 * MARKET_MAP below AND add '/en-se' to KNOWN_PREFIXES.
 */
(function () {
  'use strict';

  /* ─────────────────────────────────────────────────────────────────────────
   * CONFIGURATION
   * ───────────────────────────────────────────────────────────────────────── */

  /**
   * All known market URL path prefixes.
   * A URL that starts with any of these is already on the correct market page -
   * the redirect step is skipped entirely.
   * DK default market has NO prefix (path starts with '/') but we handle that
   * via the MARKET_MAP 'DK' entry returning '/'.
   */
  var KNOWN_PREFIXES = [
    '/en-eu',
    '/en-no',
    '/en-gb',
    '/en-na',
    '/en-au',
    '/en-se',  // Sweden - dedicated market, confirmed by Marcus 2026-04-18
    '/da-dk'  // DK explicit Danish locale prefix, if Shopify generates it
  ];

  /**
   * Country ISO 3166-1 alpha-2 → market path mapping.
   * Each entry: [marketPath, countryNameEN]
   * countryNameEN is used only in toast copy - approximate, English only.
   *
   * EU catch-all: all EU/EEA countries not in a dedicated market map to /en-eu.
   * Sweden (SE) has no own Shopify market → /en-eu.
   * Lithuania (LT) is confirmed in EU market → /en-eu.
   */
  var MARKET_MAP = {
    // ── Denmark (default market, no prefix) ──
    DK: '/',

    // ── EU market (/en-eu) ──
    // Full EU member states + EEA + select European countries in the EU market
    AL: '/en-eu', // Albania
    AT: '/en-eu', // Austria
    BE: '/en-eu', // Belgium
    BG: '/en-eu', // Bulgaria
    CH: '/en-eu', // Switzerland
    CY: '/en-eu', // Cyprus
    CZ: '/en-eu', // Czech Republic
    DE: '/en-eu', // Germany
    EE: '/en-eu', // Estonia
    ES: '/en-eu', // Spain
    FI: '/en-eu', // Finland
    FR: '/en-eu', // France
    GR: '/en-eu', // Greece
    HR: '/en-eu', // Croatia
    HU: '/en-eu', // Hungary
    IE: '/en-eu', // Ireland
    IS: '/en-eu', // Iceland
    IT: '/en-eu', // Italy
    LI: '/en-eu', // Liechtenstein
    LT: '/en-eu', // Lithuania (confirmed in EU market by Marcus)
    LU: '/en-eu', // Luxembourg
    LV: '/en-eu', // Latvia
    MT: '/en-eu', // Malta
    NL: '/en-eu', // Netherlands
    PL: '/en-eu', // Poland
    PT: '/en-eu', // Portugal
    RO: '/en-eu', // Romania
    SE: '/en-se', // Sweden - own market, confirmed by Marcus 2026-04-18
    SI: '/en-eu', // Slovenia
    SK: '/en-eu', // Slovakia

    // ── Norway market (/en-no) ──
    NO: '/en-no',

    // ── GB market (/en-gb) ──
    GB: '/en-gb',

    // ── North America market (/en-na) ──
    US: '/en-na',
    CA: '/en-na',

    // ── Australia-New Zealand market (/en-au) ──
    AU: '/en-au',
    NZ: '/en-au'
  };

  /**
   * Human-readable country names for toast copy (EN only).
   * Only needed for countries that have a dedicated market (for the toast message).
   */
  var COUNTRY_NAMES = {
    DK: 'Denmark',    NO: 'Norway',      SE: 'Sweden',
    GB: 'United Kingdom', US: 'the USA', CA: 'Canada',
    AU: 'Australia',  NZ: 'New Zealand',
    DE: 'Germany',    FR: 'France',      NL: 'Netherlands',
    BE: 'Belgium',    AT: 'Austria',     IT: 'Italy',
    ES: 'Spain',      PT: 'Portugal',    IE: 'Ireland',
    PL: 'Poland',     CZ: 'Czech Republic', FI: 'Finland',
    LT: 'Lithuania',  LV: 'Latvia',      EE: 'Estonia',
    CH: 'Switzerland', HR: 'Croatia',    HU: 'Hungary',
    RO: 'Romania',    BG: 'Bulgaria',    SK: 'Slovakia',
    SI: 'Slovenia',   GR: 'Greece',      CY: 'Cyprus',
    MT: 'Malta',      LU: 'Luxembourg',  IS: 'Iceland',
    LI: 'Liechtenstein', AL: 'Albania'
  };

  /* ─────────────────────────────────────────────────────────────────────────
   * UTILITY HELPERS
   * ───────────────────────────────────────────────────────────────────────── */

  /** Safe localStorage getter - returns null on SecurityError (private browsing). */
  function lsGet(key) {
    try { return localStorage.getItem(key); } catch (e) { return null; }
  }

  /** Safe localStorage setter. Returns false if unavailable. */
  function lsSet(key, val) {
    try { localStorage.setItem(key, val); return true; } catch (e) { return false; }
  }

  /** Safe localStorage remover. */
  function lsRemove(key) {
    try { localStorage.removeItem(key); } catch (e) {}
  }

  /** Safe sessionStorage getter. */
  function ssGet(key) {
    try { return sessionStorage.getItem(key); } catch (e) { return null; }
  }

  /** Safe sessionStorage setter. */
  function ssSet(key, val) {
    try { sessionStorage.setItem(key, val); return true; } catch (e) { return false; }
  }

  /**
   * Read a cookie by name. Returns the decoded value or null.
   * @param {string} name
   * @returns {string|null}
   */
  function getCookie(name) {
    var match = document.cookie.match(new RegExp('(?:^|;\\s*)' + name + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : null;
  }

  /**
   * Write a cookie.
   * @param {string} name
   * @param {string} value
   * @param {number} days
   */
  function setCookie(name, value, days) {
    var expires = new Date(Date.now() + days * 24 * 3600 * 1000).toUTCString();
    document.cookie = name + '=' + encodeURIComponent(value) +
      '; expires=' + expires + '; path=/; SameSite=Lax';
  }

  /**
   * Check a localStorage key that stores a Unix-ms timestamp string.
   * Returns true if the key exists and is within ttlMs of now.
   * @param {string} key
   * @param {number} ttlMs
   * @returns {boolean}
   */
  function lsWithinTTL(key, ttlMs) {
    var raw = lsGet(key);
    if (!raw) return false;
    var ts = parseInt(raw, 10);
    return !isNaN(ts) && (Date.now() - ts < ttlMs);
  }

  /**
   * Get the market path prefix of the current URL.
   * Returns the matching prefix string (e.g. '/en-eu') or '' if none matches.
   * @returns {string}
   */
  function currentPrefix() {
    var path = window.location.pathname;
    for (var i = 0; i < KNOWN_PREFIXES.length; i++) {
      if (path === KNOWN_PREFIXES[i] || path.indexOf(KNOWN_PREFIXES[i] + '/') === 0) {
        return KNOWN_PREFIXES[i];
      }
    }
    return '';
  }

  /**
   * Build a target URL for a given market path prefix.
   * The current path has its existing prefix stripped, then the new prefix prepended.
   * @param {string} targetMarketPath  e.g. '/en-eu'
   * @returns {string}  full URL
   */
  function buildRedirectUrl(targetMarketPath) {
    var existing = currentPrefix();
    var strippedPath = window.location.pathname;
    if (existing) {
      strippedPath = strippedPath.slice(existing.length) || '/';
    }
    // DK default market has no prefix - just use the stripped path.
    var newPath = (targetMarketPath === '/') ? strippedPath : targetMarketPath + strippedPath;
    return window.location.origin + newPath + window.location.search + window.location.hash;
  }

  /* ─────────────────────────────────────────────────────────────────────────
   * TOAST (Task E)
   * ───────────────────────────────────────────────────────────────────────── */

  /**
   * Show the redirect toast.
   * Called after landing on a redirected market URL, only once per session.
   * @param {string} countryIso  e.g. 'NO'
   */
  function showRedirectToast(countryIso) {
    if (ssGet('mayas_geo_toast_shown') === 'true') return;
    ssSet('mayas_geo_toast_shown', 'true');

    var isDK = (currentPrefix() === '' || currentPrefix() === '/da-dk' || countryIso === 'DK');
    var countryName = COUNTRY_NAMES[countryIso] || countryIso;

    // Copy strings - reuse TRANSLATIONS keys from geo-popup where available.
    // Line 1: "Shopping in {country} · English" (or Danish equivalent).
    // Duties line: drawn from geo-popup TRANSLATIONS.duties values.
    // Row 2: "Not right? [Change]" / "Ikke rigtigt? [Skift]"
    var line1, dutiesLine, notRight, changeLabel, closeLabel;
    if (isDK) {
      line1 = 'Handler i Danmark \u00b7 Dansk';
      dutiesLine = 'Moms, told og afgifter er inkluderet';
      notRight = 'Ikke rigtigt?';
      changeLabel = 'Skift';
      closeLabel = 'Luk';
    } else {
      line1 = 'Shopping in ' + countryName + ' \u00b7 English';
      dutiesLine = 'VAT, duties & customs included';
      notRight = 'Not right?';
      changeLabel = 'Change';
      closeLabel = 'Dismiss';
    }

    var toast = document.createElement('div');
    toast.id = 'mayas-geo-toast';
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    toast.innerHTML =
      '<div class="mayas-geo-toast__row-1">' +
        line1 +
        '<span class="mayas-geo-toast__duties">' + dutiesLine + '</span>' +
      '</div>' +
      '<div class="mayas-geo-toast__row-2">' +
        '<span>' + notRight + '</span>' +
        '<button type="button" class="mayas-geo-toast__change">' + changeLabel + '</button>' +
        '<button type="button" class="mayas-geo-toast__close" aria-label="' + closeLabel + '">\u00d7</button>' +
      '</div>';
    document.body.appendChild(toast);

    function dismissToast() {
      // Add leaving class to trigger slide-down animation, then remove from DOM.
      if (!toast.parentNode) return;
      toast.classList.add('mayas-geo-toast--leaving');
      setTimeout(function () {
        if (toast.parentNode) toast.parentNode.removeChild(toast);
      }, 280); // matches animation duration in CSS
    }

    // Auto-dismiss after 8 seconds
    var autoTimer = setTimeout(dismissToast, 8000);

    // Dismiss on scroll
    function onScroll() {
      clearTimeout(autoTimer);
      dismissToast();
      window.removeEventListener('scroll', onScroll, { passive: true });
    }
    window.addEventListener('scroll', onScroll, { passive: true });

    // Close button
    toast.querySelector('.mayas-geo-toast__close').addEventListener('click', function () {
      clearTimeout(autoTimer);
      dismissToast();
    });

    // Change button - open the existing country selector
    toast.querySelector('.mayas-geo-toast__change').addEventListener('click', function () {
      clearTimeout(autoTimer);
      dismissToast();
      // Open the localization-toggle dropdown (desktop selector)
      var triggers = document.querySelectorAll('.localization-toggle');
      for (var i = 0; i < triggers.length; i++) {
        var rect = triggers[i].getBoundingClientRect();
        if (rect.width > 0 && rect.height > 0) {
          triggers[i].click();
          return;
        }
      }
      // Fallback: open mobile locale details
      var mobileDetails = document.querySelector('.HeaderLocaleMobile__details');
      if (mobileDetails) mobileDetails.open = true;
    });
  }

  /* ─────────────────────────────────────────────────────────────────────────
   * BANNER (Task D) - Unmatched-country visitors
   * ───────────────────────────────────────────────────────────────────────── */

  /**
   * Show the unmatched-country banner once per 30 days.
   * Visitors whose country has no configured market see this banner
   * and are served the default DK English content.
   */
  function showUnmatchedBanner() {
    if (lsWithinTTL('mayas_geo_banner_dismissed', 30 * 24 * 3600 * 1000)) return;

    // Copy: primary line tells the visitor what they're seeing; duties line confirms
    // final pricing so they aren't put off by uncertainty around import charges.
    var banner = document.createElement('div');
    banner.id = 'mayas-geo-banner';
    banner.innerHTML =
      '<span class="mayas-geo-banner__text">' +
        'Shopping in English \u2014 your region isn\'t set up yet.' +
        '<span class="mayas-geo-banner__duties">VAT, duties &amp; customs included</span>' +
      '</span>' +
      '<button type="button" class="mayas-geo-banner__change">Change region</button>' +
      '<button type="button" class="mayas-geo-banner__close" aria-label="Close">\u00d7</button>';
    document.body.appendChild(banner);

    function dismissBanner() {
      lsSet('mayas_geo_banner_dismissed', String(Date.now()));
      if (banner.parentNode) banner.parentNode.removeChild(banner);
    }

    banner.querySelector('.mayas-geo-banner__close').addEventListener('click', dismissBanner);

    banner.querySelector('.mayas-geo-banner__change').addEventListener('click', function () {
      dismissBanner();
      // Open country selector
      var triggers = document.querySelectorAll('.localization-toggle');
      for (var i = 0; i < triggers.length; i++) {
        var rect = triggers[i].getBoundingClientRect();
        if (rect.width > 0 && rect.height > 0) {
          triggers[i].click();
          return;
        }
      }
      var mobileDetails = document.querySelector('.HeaderLocaleMobile__details');
      if (mobileDetails) mobileDetails.open = true;
    });
  }

  /* ─────────────────────────────────────────────────────────────────────────
   * EXPLICIT-CHOICE LISTENER
   * Marks the user as having chosen a market manually (via any localization
   * selector button click) - suppresses auto-redirect for 90 days.
   * ───────────────────────────────────────────────────────────────────────── */

  function attachExplicitChoiceListeners() {
    // localization-selector.liquid - desktop country selector
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[name="country_code"], [name="locale_code"]');
      if (!btn) return;
      // Any country selector submit = explicit user choice
      lsSet('mayas_geo_user_explicit', String(Date.now()));
      // Update the market pref cookie to match what the user just chose
      var country = btn.value;
      if (country && MARKET_MAP[country]) {
        setCookie('mayas_market_pref', MARKET_MAP[country], 180);
      }
    });
  }

  /* ─────────────────────────────────────────────────────────────────────────
   * MAIN DECISION TREE
   * ───────────────────────────────────────────────────────────────────────── */

  function init() {
    attachExplicitChoiceListeners();

    // ── Step 0: Localhost / dev-server bypass ──
    // The shopify theme dev server (127.0.0.1, localhost, *.shopifypreview.com)
    // doesn't have working /localization form posts, so manual market switches
    // get overridden by IP-detection redirects. Skip auto-redirect entirely on
    // dev hostnames so Marcus can manually test every market locally.
    // Production hostnames still hit the full redirect logic below.
    var hostname = window.location.hostname;
    if (hostname === 'localhost' ||
        hostname === '127.0.0.1' ||
        hostname.endsWith('.local') ||
        hostname.endsWith('.shopifypreview.com')) {
      return;
    }

    // ── Step 1: Loop guard - bail if we've redirected too many times ──
    // Declared early because it's needed before any redirect attempt below.
    var redirectCount = parseInt(ssGet('mayas_geo_redirect_count') || '0', 10);
    if (redirectCount >= 2) {
      console.warn('[geo-language] Redirect loop guard triggered - aborting.');
      return;
    }

    // ── Step 2: Check if we just landed after a redirect (toast display) ──
    if (ssGet('mayas_geo_just_redirected') === 'true') {
      // Clear the flag so it doesn't persist to the next page within this session.
      ssSet('mayas_geo_just_redirected', '');
      // Show toast - reverse-lookup country ISO from the market_pref cookie.
      var prefCookie = getCookie('mayas_market_pref');
      var toastIso = 'EU'; // Fallback ISO for toast copy
      if (prefCookie) {
        // Use the *current* URL prefix to find a representative ISO code for
        // the toast copy. First match wins (e.g. /en-eu → DE as representative EU).
        for (var iso in MARKET_MAP) {
          if (MARKET_MAP.hasOwnProperty(iso) && MARKET_MAP[iso] === prefCookie) {
            toastIso = iso;
            break;
          }
        }
      }
      showRedirectToast(toastIso);
      return; // Already on the right market - nothing else to do this page load.
    }

    // ── Step 3: Returning visitor with a saved preference ──
    // The cookie's 180-day TTL is enforced by the browser via the `expires=`
    // attribute written in setCookie(). A truthy value here means the cookie
    // is still within its window. Policy: URL wins over cookie when they
    // disagree - a visitor who deliberately clicks a link into a specific
    // market locale must not be overridden by stale cookie state.
    var prefix = currentPrefix();
    var marketPrefCookie = getCookie('mayas_market_pref');
    if (marketPrefCookie) {
      if (prefix !== '') {
        // On a known market URL + saved choice → respect the URL.
        return;
      }
      if (marketPrefCookie !== '/') {
        // No URL prefix, cookie points to a non-default market → redirect.
        ssSet('mayas_geo_redirect_count', String(redirectCount + 1));
        ssSet('mayas_geo_just_redirected', 'true');
        window.location.replace(buildRedirectUrl(marketPrefCookie));
        return;
      }
      // Cookie is '/' (DK default) and URL has no prefix → already correct.
      return;
    }

    // ── Step 4: Explicit-choice localStorage flag (90-day TTL) ──
    // Covers the cookie-cleared-but-localStorage-retained edge case.
    if (lsWithinTTL('mayas_geo_user_explicit', 90 * 24 * 3600 * 1000)) {
      return;
    }

    // ── Step 5: UTM note ──
    // Per Marcus's answer Q6: redirect ALL traffic including UTM/ad traffic.
    // Cookie is set on this visit so subsequent loads hit the fast path.

    // ── Step 6: First-time visitor - fetch IP country and reconcile against URL ──
    // Unlike the previous implementation, this runs even when the URL already
    // has a known market prefix. That lets us correct Shopify Markets' native
    // browser-language routing (e.g. a DK-IP visitor pushed onto /en-gb/
    // because the Accept-Language header said en-GB).
    fetch('/browsing_context_suggestions.json')
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      })
      .then(function (res) {
        var detected = res && res.detected_values && res.detected_values.country;
        if (!detected || !detected.handle) return;
        var detectedIso = (detected.handle || '').toUpperCase();

        // ── Step 7: Map detected country to target market path ──
        var targetPath = MARKET_MAP[detectedIso]; // undefined = unmatched

        if (targetPath === undefined) {
          // Unmatched country - pin to EU English and show banner.
          setCookie('mayas_market_pref', '/en-eu', 180);
          showUnmatchedBanner();
          return;
        }

        // ── Step 8: Compare target to current URL (reuses `prefix` from Step 3) ──
        if (targetPath === '/' && prefix === '') {
          setCookie('mayas_market_pref', '/', 180);
          return;
        }
        if (targetPath === prefix) {
          setCookie('mayas_market_pref', targetPath, 180);
          return;
        }

        // ── Step 9: Redirect ──
        // Cookie written BEFORE redirect so a failed redirect still populates
        // the fast path on the next visit.
        setCookie('mayas_market_pref', targetPath, 180);
        ssSet('mayas_geo_redirect_count', String(redirectCount + 1));
        ssSet('mayas_geo_just_redirected', 'true');

        var newUrl = buildRedirectUrl(targetPath);
        window.location.replace(newUrl);
      })
      .catch(function (err) {
        console.warn('[geo-language] /browsing_context_suggestions.json failed:', err);
        // Fail silently - never break the page.
      });
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Module-load sanity assert
  // Every MARKET_MAP value must be '/' (DK default) or present in
  // KNOWN_PREFIXES. Drift between the two lists would break the IP-reconcile
  // logic (a redirect could land on a URL whose prefix doesn't match any
  // entry in KNOWN_PREFIXES, causing the next visit to loop via the IP path).
  // Non-fatal warning so future maintenance errors surface in devtools.
  // ─────────────────────────────────────────────────────────────────────────
  (function assertMarketMapConsistency() {
    var unknown = [];
    for (var iso in MARKET_MAP) {
      if (!MARKET_MAP.hasOwnProperty(iso)) continue;
      var path = MARKET_MAP[iso];
      if (path === '/') continue;
      if (KNOWN_PREFIXES.indexOf(path) === -1) {
        unknown.push(iso + '→' + path);
      }
    }
    if (unknown.length) {
      console.warn(
        '[geo-language] MARKET_MAP/KNOWN_PREFIXES drift detected. ' +
        'These entries map to paths not in KNOWN_PREFIXES: ' + unknown.join(', ') +
        '. Update KNOWN_PREFIXES or MARKET_MAP so the two lists agree.'
      );
    }
  })();

  // Run on DOMContentLoaded (or immediately if DOM is already ready).
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();

/*
 * ─────────────────────────────────────────────────────────────────────────────
 * STORAGE KEYS REFERENCE TABLE
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Key                          | Storage       | Value                    | TTL        | Scope
 * -----------------------------|---------------|--------------------------|------------|----------
 * mayas_market_pref            | Cookie        | market path e.g /en-eu  | 180 days   | all pages
 * mayas_geo_user_explicit      | localStorage  | Unix ms timestamp        | 90 days    | browser
 * mayas_geo_banner_dismissed   | localStorage  | Unix ms timestamp        | 30 days    | browser
 * mayas_geo_just_redirected    | sessionStorage| 'true' / ''             | session    | tab
 * mayas_geo_toast_shown        | sessionStorage| 'true'                   | session    | tab
 * mayas_geo_redirect_count     | sessionStorage| integer string           | session    | tab
 *
 * Keys from geo-popup.liquid (for cross-reference):
 * mayas_geo_popup_dismissed    | localStorage  | Unix ms timestamp        | 30 days    | browser
 * ─────────────────────────────────────────────────────────────────────────────
 */
